import Component from "@glimmer/component";
import { block } from "discourse/blocks";
import AsyncContent from "discourse/components/async-content";
import DButton from "discourse/components/d-button";
import { ajax } from "discourse/lib/ajax";
import { bind } from "discourse/lib/decorators";
import { i18n } from "discourse-i18n";
import { eventDetailUrl } from "../lib/dec-event-url";

function pad(value) {
  return `${value}`.padStart(2, "0");
}

function dateParam(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function compactDate(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.toLocaleString(undefined, { month: "short" })} ${date.getDate()}`;
}

function compactTimeRange(startsAt, endsAt) {
  const start = new Date(startsAt);
  const end = new Date(endsAt || startsAt);

  if (Number.isNaN(start.getTime())) {
    return "";
  }

  const options = { hour: "numeric", minute: "2-digit" };
  const startLabel = start.toLocaleTimeString(undefined, options);

  if (Number.isNaN(end.getTime())) {
    return startLabel;
  }

  return `${startLabel} - ${end.toLocaleTimeString(undefined, options)}`;
}

@block("discourse-events-calendar:upcoming-events", {
  description: "Compact list of visible upcoming calendar events",
  args: {
    count: { type: "number", default: 5 },
  },
})
export default class DecUpcomingEvents extends Component {
  @bind
  async fetchEvents() {
    const count = Math.max(1, Math.min(this.args.count || 5, 10));
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth() + 3, start.getDate());
    const response = await ajax("/calendar/events.json", {
      data: {
        filter: "upcoming",
        start: dateParam(start),
        end: dateParam(end),
        limit: count,
        summary: true,
      },
    });

    return response?.events?.length ? response.events : null;
  }

  compactDate(startsAt) {
    return compactDate(startsAt);
  }

  compactTimeRange(startsAt, endsAt) {
    return compactTimeRange(startsAt, endsAt);
  }

  eventMeta(event) {
    return [compactTimeRange(event.starts_at, event.ends_at), event.room?.name || event.location]
      .filter(Boolean)
      .join(" · ");
  }

  eventUrl(event) {
    return eventDetailUrl(event);
  }

  <template>
    <section class="discourse-events-calendar__upcoming-block">
      <div class="discourse-events-calendar__upcoming-block-header">
        <h3>{{i18n "discourse_events_calendar.upcoming_title"}}</h3>
        <a href="/calendar">{{i18n "discourse_events_calendar.upcoming_view_all"}}</a>
      </div>

      <AsyncContent @asyncData={{this.fetchEvents}}>
        <:loading>
          <div class="discourse-events-calendar__upcoming-block-loading">
            <div class="spinner" />
          </div>
        </:loading>

        <:empty>
          <p class="discourse-events-calendar__upcoming-block-empty">
            {{i18n "discourse_events_calendar.upcoming_empty"}}
          </p>
        </:empty>

        <:content as |events|>
          <ol class="discourse-events-calendar__upcoming-block-list">
            {{#each events as |event|}}
              <li>
                <a href={{this.eventUrl event}} class="discourse-events-calendar__upcoming-block-event">
                  <span class="discourse-events-calendar__upcoming-block-date">
                    {{this.compactDate event.starts_at}}
                  </span>
                  <span class="discourse-events-calendar__upcoming-block-main">
                    <strong>{{event.title}}</strong>
                    <em>{{this.eventMeta event}}</em>
                  </span>
                </a>
              </li>
            {{/each}}
          </ol>

          <DButton
            class="btn-default discourse-events-calendar__upcoming-block-button"
            @href="/calendar"
            @label="discourse_events_calendar.upcoming_view_all"
          />
        </:content>
      </AsyncContent>
    </section>
  </template>
}
